
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type TaaShaderUniforms = {
	uCurrentColor: WebGLUniformLocation,
	uHistoryColor: WebGLUniformLocation,
	uMotionMap: WebGLUniformLocation,
	uTexelSize: WebGLUniformLocation,
	uBlendAlpha: WebGLUniformLocation,
	uHistoryValid: WebGLUniformLocation
};

export type TaaShaderAttributes = {
	aVertexPosition: number,
	vTextureCoord: number
};

export type TaaShaderSettings = string[];

export class TaaShader extends ShaderBase<TaaShaderUniforms, TaaShaderAttributes> {

    private static fragmentShaderSource = `
precision highp float;

in vec2 vTextureCoord;
out vec4 fragColor;

uniform sampler2D uCurrentColor;
uniform sampler2D uHistoryColor;
uniform sampler2D uMotionMap;
uniform vec2 uTexelSize;
uniform float uBlendAlpha;
uniform int uHistoryValid;

// --- begin: temporal_reproject.glsl ---
// Shared helpers for temporal accumulation passes (TAA, future temporal SSAO,
// volumetric, bloom). Reprojects history samples via screen-space motion vectors
// and clamps them against the current frame's local color neighborhood to
// suppress ghosting on disocclusion or wrong reprojection.

// Reproject the current pixel's UV back to where it was last frame.
vec2 reprojectUV(vec2 currentUV, vec2 motion) {
    return currentUV + motion;
}

bool insideUV(vec2 uv) {
    return all(greaterThanEqual(uv, vec2(0.0))) && all(lessThanEqual(uv, vec2(1.0)));
}

// Min/max color across the 3x3 neighborhood of the current pixel. Used to
// clamp the reprojected history sample, which trades a small amount of blur
// for a large reduction in ghosting.
void neighborhoodAABB(
    sampler2D currentColor,
    vec2 uv,
    vec2 texelSize,
    out vec3 minC,
    out vec3 maxC
) {
    minC = vec3(1e10);
    maxC = vec3(-1e10);
    for (int y = -1; y <= 1; ++y) {
        for (int x = -1; x <= 1; ++x) {
            vec3 c = texture(currentColor, uv + vec2(float(x), float(y)) * texelSize).rgb;
            minC = min(minC, c);
            maxC = max(maxC, c);
        }
    }
}

// Variance clipping (Karis 2014): bound the history sample by the *statistical*
// neighborhood instead of the raw min/max. Computes mean μ and std-dev σ over
// the 3x3 neighborhood of \`uv\`, returns clipMin = μ − γσ and clipMax = μ + γσ.
//
// The raw box clamp (\`neighborhoodAABB\`) admits any history value that falls
// inside the per-channel min/max box of the neighborhood — which is generous
// when the neighborhood is broad (high noise, fine specular detail) and lets
// stale history slip through during slow lighting changes (e.g. day-night
// cycle while the camera is static — geometry doesn't move so motion vectors
// are zero, reproject lands on the same pixel, and the slowly-shifting min/max
// barely moves frame to frame). Variance clipping tightens this: γ ≈ 1.0
// rejects anything outside one standard deviation of the local distribution,
// which is much more aggressive at evicting stale lighting while still
// preserving the natural per-frame jitter from variance-fed effects (PCF /
// SSAO temporal rotation) since their variance lives within the σ envelope.
//
// Reference: Karis, "High-Quality Temporal Supersampling" (SIGGRAPH 2014).
void neighborhoodVarianceClip(
    sampler2D currentColor,
    vec2 uv,
    vec2 texelSize,
    float gamma,
    out vec3 clipMin,
    out vec3 clipMax
) {
    vec3 m1 = vec3(0.0);
    vec3 m2 = vec3(0.0);
    for (int y = -1; y <= 1; ++y) {
        for (int x = -1; x <= 1; ++x) {
            vec3 c = texture(currentColor, uv + vec2(float(x), float(y)) * texelSize).rgb;
            m1 += c;
            m2 += c * c;
        }
    }
    vec3 mean = m1 * (1.0 / 9.0);
    vec3 var = max(m2 * (1.0 / 9.0) - mean * mean, vec3(0.0));
    vec3 sigma = sqrt(var);
    clipMin = mean - gamma * sigma;
    clipMax = mean + gamma * sigma;
}


// --- end: temporal_reproject.glsl ---

void main(void) {
    vec3 current = texture(uCurrentColor, vTextureCoord).rgb;

    // First frame, scene change, or large camera teleport: skip blending.
    if (uHistoryValid == 0) {
        fragColor = vec4(current, 1.0);
        return;
    }

    vec2 motion = texture(uMotionMap, vTextureCoord).xy;
    vec2 historyUV = reprojectUV(vTextureCoord, motion);
    if (!insideUV(historyUV)) {
        fragColor = vec4(current, 1.0);
        return;
    }

    vec3 history = texture(uHistoryColor, historyUV).rgb;

    // Variance clip (Karis 2014, gamma = 1.0): bound history by mean ± sigma over the
    // 3x3 neighborhood. Tighter than a min/max box clamp — evicts stale lighting from
    // the day-night cycle (which slips through the broad box on static cameras) while
    // preserving per-frame jitter from PCF/SSAO temporal rotation, whose variance lives
    // within the sigma envelope.
    vec3 clipMin, clipMax;
    neighborhoodVarianceClip(uCurrentColor, vTextureCoord, uTexelSize, 1.0, clipMin, clipMax);
    history = clamp(history, clipMin, clipMax);

    fragColor = vec4(mix(current, history, 1.0 - uBlendAlpha), 1.0);
}
`;

    private static vertexShaderSource = `
layout(location = 0) in vec2 aVertexPosition;

out vec2 vTextureCoord;

const vec2 scale = vec2(0.5, 0.5);

void main()
{
    vTextureCoord = aVertexPosition * scale + scale;
    gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: TaaShaderUniformsContainer;
    public attributesContainer: TaaShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: TaaShaderSettings) {
        super(ctx, TaaShader.fragmentShaderSource, TaaShader.vertexShaderSource, settings);
        this.uniformsContainer = new TaaShaderUniformsContainer(ctx, this);
        this.attributesContainer = new TaaShaderShaderAttributesContainer(ctx, this);
    }
}

class TaaShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: TaaShader) {
        super(ctx);
    }

    public setCurrentColor(texture: Texture) : TaaShaderUniformsContainer {
        this.setTextureValue('uCurrentColor', this.shader.uniforms.uCurrentColor, texture, 'sampler2D');
        return this;
    }

    public setHistoryColor(texture: Texture) : TaaShaderUniformsContainer {
        this.setTextureValue('uHistoryColor', this.shader.uniforms.uHistoryColor, texture, 'sampler2D');
        return this;
    }

    public setMotionMap(texture: Texture) : TaaShaderUniformsContainer {
        this.setTextureValue('uMotionMap', this.shader.uniforms.uMotionMap, texture, 'sampler2D');
        return this;
    }

    public setTexelSize(value: vec2 | number[]) : TaaShaderUniformsContainer {
        this.setValue('uTexelSize', this.shader.uniforms.uTexelSize, value, 'vec2');
        return this;
    }

    public setBlendAlpha(value: number) : TaaShaderUniformsContainer {
        this.setValue('uBlendAlpha', this.shader.uniforms.uBlendAlpha, value, 'float');
        return this;
    }

    public setHistoryValid(value: number) : TaaShaderUniformsContainer {
        this.setValue('uHistoryValid', this.shader.uniforms.uHistoryValid, value, 'int');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uCurrentColor', uniform: this.shader.uniforms.uCurrentColor, dataType: 'sampler2D' },
			{ name: 'uHistoryColor', uniform: this.shader.uniforms.uHistoryColor, dataType: 'sampler2D' },
			{ name: 'uMotionMap', uniform: this.shader.uniforms.uMotionMap, dataType: 'sampler2D' }
        ];
    }
}

class TaaShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: TaaShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : TaaShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTextureCoord(settings: { length: number, offset: number, itemSize: number }) : TaaShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTextureCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}